import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '@/utils/toast-utils';

interface VerifyOtpFormProps {
  email: string;
  type: 'signup' | 'recovery';
  returnTo?: string;
  onSuccess: () => void;
}

export const VerifyOtpForm: React.FC<VerifyOtpFormProps> = ({ 
  email, 
  type, 
  returnTo,
  onSuccess
}) => {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      console.log(`Verifying OTP for ${email} with type: ${type}`);
      
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: type,
      });

      if (verifyError) throw verifyError;

      console.log(`OTP Verification successful for type: ${type}`, data);
      
      // Call the onSuccess callback provided by the parent component
      onSuccess();
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.message || "Invalid or expired verification code");
      showErrorToast({ 
        title: "Verification Failed", 
        description: err.message || "Invalid or expired verification code"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendDisabled) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (type === 'signup') {
        // For signup, generate a new OTP
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
        });
        
        if (error) throw error;
      } else if (type === 'recovery') {
        // For password reset, request a new OTP
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        
        if (error) throw error;
      }
      
      showSuccessToast({
        title: "Code Resent",
        description: "A new verification code has been sent to your email"
      });
      
      // Disable resend button for 60 seconds
      setResendDisabled(true);
      setCountdown(60);
      
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err: any) {
      console.error('Resend code error:', err);
      setError(err.message || "Failed to resend verification code");
      showErrorToast({
        title: "Error",
        description: err.message || "Failed to resend verification code"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-center">
        <InputOTP maxLength={6} value={otp} onChange={setOtp}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Button 
        type="submit" 
        className="w-full" 
        disabled={isLoading || otp.length !== 6}
      >
        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {isLoading ? 'Verifying...' : 'Verify'}
      </Button>
      
      <div className="text-center">
        <Button 
          type="button" 
          variant="link" 
          className="text-secondary" 
          onClick={handleResendCode}
          disabled={resendDisabled || isLoading}
        >
          {resendDisabled 
            ? `Resend code in ${countdown}s` 
            : "Didn't receive a code? Resend"}
        </Button>
      </div>
    </form>
  );
};
